/**
 * Runtime polyfills for the app entry point.
 *
 * Import this module first, before any application code, so the polyfills are
 * installed before any component renders.
 */

/**
 * ES2025 Set methods (union, intersection, difference, symmetricDifference,
 * isSubsetOf, isSupersetOf, isDisjointFrom).
 *
 * These are declared globally in
 * `packages/shared-config/config/typings/set-extensions.d.ts` and used by the
 * permission-accordion containers, but they only ship natively in Safari 17+,
 * Chrome 122+, and Firefox 127+. Users on older browsers (e.g. Safari 15.6) hit
 * "isSupersetOf is not a function" and the page white-screens. Install
 * spec-compatible implementations only when the engine lacks them.
 *
 * All call sites pass real `Set` instances, so these implementations operate on
 * `Set` arguments rather than the fuller spec "set-like" protocol.
 */
const installSetMethod = <T>(
  name: string,
  value: (this: Set<T>, other: Set<T>) => Set<T> | boolean
): void => {
  if (name in Set.prototype) return
  Object.defineProperty(Set.prototype, name, {
    value,
    writable: true,
    enumerable: false,
    configurable: true,
  })
}

installSetMethod('union', function union<T>(this: Set<T>, other: Set<T>): Set<T> {
  const result = new Set<T>(this)
  for (const element of other) result.add(element)
  return result
})

installSetMethod('intersection', function intersection<T>(this: Set<T>, other: Set<T>): Set<T> {
  const result = new Set<T>()
  for (const element of this) {
    if (other.has(element)) result.add(element)
  }
  return result
})

installSetMethod('difference', function difference<T>(this: Set<T>, other: Set<T>): Set<T> {
  const result = new Set<T>(this)
  for (const element of other) result.delete(element)
  return result
})

installSetMethod('symmetricDifference', function symmetricDifference<
  T,
>(this: Set<T>, other: Set<T>): Set<T> {
  const result = new Set<T>(this)
  for (const element of other) {
    if (this.has(element)) result.delete(element)
    else result.add(element)
  }
  return result
})

installSetMethod('isSubsetOf', function isSubsetOf<T>(this: Set<T>, other: Set<T>): boolean {
  if (this.size > other.size) return false
  for (const element of this) {
    if (!other.has(element)) return false
  }
  return true
})

installSetMethod('isSupersetOf', function isSupersetOf<T>(this: Set<T>, other: Set<T>): boolean {
  if (this.size < other.size) return false
  for (const element of other) {
    if (!this.has(element)) return false
  }
  return true
})

installSetMethod('isDisjointFrom', function isDisjointFrom<
  T,
>(this: Set<T>, other: Set<T>): boolean {
  for (const element of this) {
    if (other.has(element)) return false
  }
  return true
})
